import { useRef, useState } from 'react';
import { createTemplateFromFiles } from 'services/escriturasTemplatesService';

const MAX_FILES = 12;

/**
 * Genera una plantilla desde documentos. El TIPO ya viene fijado (la fila desde la que
 * se abrió) — el usuario solo sube los escritos de ejemplo. name/description se derivan
 * del tipo en el backend. Fases: confirm → generating → (cierra con éxito) | error.
 */
export default function TemplateCreateModal({ DesignSystemComponents, writingType, t, onClose, onCreated }) {
  const { Modal, Button } = DesignSystemComponents ?? {};

  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState('confirm'); // confirm | generating | error
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  // Anti doble-click: se setea SINCRÓNICAMENTE (antes del await), no espera al re-render.
  const submittingRef = useRef(false);

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, MAX_FILES);
    setFiles(picked);
  };

  const handleSubmit = async () => {
    if (files.length === 0 || submittingRef.current) return; // un solo envío por click
    submittingRef.current = true;
    setPhase('generating');
    setError(null);
    try {
      const result = await createTemplateFromFiles({ writingTypeId: writingType.id, files });
      if (result?.status === 'ERROR') {
        setError(result.message || t('tpl_error_generic'));
        setPhase('error');
        return;
      }
      onCreated(result);
    } catch (err) {
      // 409: ya hay una generación en curso para este tipo (single-flight del backend).
      const conflict = err.response?.status === 409;
      setError(conflict ? t('tpl_generating_conflict') : (err.response?.data?.message || err.message || t('tpl_error_generic')));
      setPhase('error');
    } finally {
      submittingRef.current = false;
    }
  };

  const body = (
    <div>
      <p className='font-light mb-2' style={{ fontSize: 13 }}>{t('tpl_create_hint')}</p>

      <div className='mb-3' style={{ fontSize: 13 }}>
        <strong>{t('tpl_create_type_label')}:</strong> {writingType.displayName} ({writingType.code})
      </div>

      {writingType.hasActiveTemplate && (
        <div className='alert alert-warning py-2' style={{ fontSize: 13 }}>
          {t('tpl_create_existing_warning', { version: writingType.activeVersion })}
        </div>
      )}

      {error && <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>{error}</div>}

      {phase === 'generating' ? (
        <p className='font-light my-3'>{t('tpl_create_generating')}</p>
      ) : (
        <>
          <label className='form-label fw-semibold' style={{ fontSize: 13 }}>
            {t('tpl_create_files_label')}
          </label>
          <div className='mb-2'>
            <input
              ref={inputRef}
              type='file'
              multiple
              accept='.pdf,.docx,.doc,.txt,.md'
              onChange={onPick}
              style={{ display: 'none' }}
            />
            {Button ? (
              <Button label={t('tpl_create_files_pick')} size='sm' variant='secondary' onClick={() => inputRef.current?.click()} />
            ) : (
              <button type='button' className='btn btn-outline-secondary btn-sm' onClick={() => inputRef.current?.click()}>
                {t('tpl_create_files_pick')}
              </button>
            )}
            {files.length > 0 && (
              <span className='ms-2' style={{ fontSize: 13 }}>{t('tpl_create_files_count', { count: files.length })}</span>
            )}
          </div>
          {files.length > 0 && (
            <ul style={{ fontSize: 12, paddingLeft: 18, maxHeight: 140, overflowY: 'auto' }}>
              {files.map((f, i) => <li key={`${f.name}-${i}`}>{f.name}</li>)}
            </ul>
          )}

          <p className='font-light mt-2' style={{ fontSize: 12, color: '#64748B' }}>{t('tpl_create_privacy')}</p>
        </>
      )}

      <div className='d-flex justify-content-end gap-2 mt-3'>
        {Button ? (
          <>
            <Button label={t('tpl_btn_cancel')} size='sm' variant='secondary' onClick={onClose} disabled={phase === 'generating'} />
            <Button
              label={phase === 'generating' ? t('tpl_create_generating') : t('tpl_create_submit')}
              size='sm'
              variant='primary'
              onClick={handleSubmit}
              disabled={phase === 'generating' || files.length === 0}
            />
          </>
        ) : (
          <>
            <button type='button' className='btn btn-outline-secondary btn-sm' onClick={onClose} disabled={phase === 'generating'}>
              {t('tpl_btn_cancel')}
            </button>
            <button type='button' className='btn btn-primary btn-sm' onClick={handleSubmit} disabled={phase === 'generating' || files.length === 0}>
              {t('tpl_create_submit')}
            </button>
          </>
        )}
      </div>
    </div>
  );

  if (!Modal) {
    return (
      <div role='dialog' style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }} onClick={onClose}>
        <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, padding: 20, width: 'min(560px, 92vw)' }}>
          <h5>{t('tpl_create_title')}</h5>
          {body}
        </div>
      </div>
    );
  }
  return (
    <Modal title={t('tpl_create_title')} show onClose={onClose} size='md'>
      {body}
    </Modal>
  );
}
